/**
 * ODC — opportunity creation modal.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, X, Save } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../components/ui/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const csvToList = (v) =>
  (v || '').split(',').map((s) => s.trim()).filter(Boolean);

const OdcOpportunityFormModal = ({ onClose, onCreated, headers }) => {
  const { toast } = useToast();
  const [draft, setDraft] = useState({
    title: '',
    summary: '',
    full_description: '',
    industry_tags: '',
    product_categories: '',
    country_origin: '',
    country_target: '',
    estimated_value: '',
    currency: 'USD',
    deadline: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!draft.title.trim() || !draft.summary.trim()) {
      toast({ variant: 'destructive', title: 'Missing required fields', description: 'Title and summary are required.' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(),
        summary: draft.summary.trim(),
        full_description: draft.full_description.trim() || undefined,
        industry_tags: csvToList(draft.industry_tags),
        product_categories: csvToList(draft.product_categories),
        country_origin: draft.country_origin.trim() || undefined,
        country_target: draft.country_target.trim() || undefined,
        estimated_value: draft.estimated_value ? Number(draft.estimated_value) : undefined,
        currency: draft.currency,
        deadline: draft.deadline || undefined,
        notes: draft.notes.trim() || undefined,
      };
      const r = await axios.post(`${API_URL}/api/odc/opportunities`, payload, { headers });
      toast({ title: 'Opportunity created', description: r.data.code });
      onCreated && onCreated(r.data);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Save failed', description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="odc-create-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <h3 className="text-base font-bold text-[#0F172A]">New Business Opportunity</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wider text-gray-500">Title *</label>
            <input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded text-sm"
              placeholder="e.g. Heavy machinery export to Angola"
              data-testid="odc-form-title"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wider text-gray-500">Executive Summary *</label>
            <textarea
              value={draft.summary}
              onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
              rows={3}
              className="mt-1 w-full px-3 py-2 border rounded text-sm"
              placeholder="2-3 sentences — what is the opportunity and why now"
              data-testid="odc-form-summary"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wider text-gray-500">Full Description (optional)</label>
            <textarea
              value={draft.full_description}
              onChange={(e) => setDraft({ ...draft, full_description: e.target.value })}
              rows={4}
              className="mt-1 w-full px-3 py-2 border rounded text-sm"
              placeholder="Detail, terms, exclusivity, etc."
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Industry tags (comma-separated)</label>
            <input
              value={draft.industry_tags}
              onChange={(e) => setDraft({ ...draft, industry_tags: e.target.value })}
              placeholder="mining, construction, oil_gas"
              className="mt-1 w-full px-3 py-2 border rounded text-sm"
              data-testid="odc-form-industry"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Product categories</label>
            <input
              value={draft.product_categories}
              onChange={(e) => setDraft({ ...draft, product_categories: e.target.value })}
              placeholder="heavy_machinery, spare_parts"
              className="mt-1 w-full px-3 py-2 border rounded text-sm"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Country origin</label>
            <input value={draft.country_origin} onChange={(e) => setDraft({ ...draft, country_origin: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded text-sm" placeholder="UAE" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Country target</label>
            <input value={draft.country_target} onChange={(e) => setDraft({ ...draft, country_target: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded text-sm" placeholder="Angola" data-testid="odc-form-target" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Estimated value</label>
            <input type="number" value={draft.estimated_value} onChange={(e) => setDraft({ ...draft, estimated_value: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded text-sm" placeholder="2500000" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Currency</label>
            <select value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded text-sm bg-white">
              {['USD', 'AED', 'EUR'].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500">Deadline</label>
            <input type="date" value={draft.deadline} onChange={(e) => setDraft({ ...draft, deadline: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wider text-gray-500">Internal notes</label>
            <textarea value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} rows={2} className="mt-1 w-full px-3 py-2 border rounded text-sm" placeholder="Visible only to admins" />
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#0F172A] text-white" data-testid="odc-form-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save opportunity
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OdcOpportunityFormModal;
